import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormInputDropdown } from '../form/FormInputDropdown';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import GolfSpotWidget from './GolfSpotWidget';
import GolfSpotDetailModal from './golfModal/GolfSpotDetailModal';
import GolfSpotBasicSettingModal from './golfModal/GolfSpotBasicSettingModal';
import GolfExtendTimeModal from './golfModal/GolfExtendTimeModal';
import GolfSpotWaitingAllListModal from './golfModal/GolfSpotWaitingAllListModal';
import GolfSpotWatingListModal from './golfModal/GolfSpotWatingListModal';
import GolfIcon from '../../_icons/GolfIcon';
import WaitingIcon from '../../_icons/WaitingIcon';
import { getAreaListByBranchId, getGolfSpotsByAreaId, updateGolfSpotReservationSpotMove } from '../../api/golf';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';

const GolfSpotManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { control, setValue, watch } = useForm({
    defaultValues: {
      selectedArea: '',
    },
  });

  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [openBasicSettingModal, setOpenBasicSettingModal] = useState(false);
  const [openExtendTimeModal, setOpenExtendTimeModal] = useState(false);
  const [openWaitingListModal, setOpenWaitingListModal] = useState(false);
  const [openSpotWaitingListModal, setOpenSpotWaitingListModal] = useState(false);
  const [isMoveMode, setIsMoveMode] = useState(false);
  const [sourceSpotForMove, setSourceSpotForMove] = useState(null);
  const [loadingSpotIds, setLoadingSpotIds] = useState([]);
  const AUTO_REFRESH_INTERVAL = 3000; // 자동 최신화 주기 (3초)
  const queryClient = useQueryClient();
  const toast = useToast();

  // 오늘 날짜 포맷팅
  const today = moment().format('YYYY년 M월 D일');
  const user = useSelector(state => state.user.user);
  const branchId = user.branch?.branchId;
  const centerId = user.headBranch?.headBranchId;

  // 구역 목록 조회 (즉시 갱신)
  const {
    data: areas = [],
    isError: isAreasError,
    error: areasError,
    refetch: refetchAreas,
  } = useQuery({
    queryKey: ['golfAreas', user?.token, branchId],
    queryFn: () => getAreaListByBranchId(user.token, branchId),
    enabled: !!user?.token && !!branchId,
    select: data => (Array.isArray(data) ? data : []),
    refetchOnWindowFocus: true, // 창 포커스 시 즉시 갱신 (다른 페이지에서 돌아올 때)
    refetchOnMount: true, // 컴포넌트 마운트 시 즉시 갱신
    staleTime: 0, // 항상 최신 데이터로 간주
  });

  // 페이지 visibility 변경 시 구역 목록 갱신 (다른 탭에서 돌아올 때)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetchAreas();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetchAreas]);

  // 첫 번째 구역 자동 선택 및 삭제된 구역 처리
  useEffect(() => {
    const currentAreaId = watch('selectedArea');

    // 구역이 없으면 선택 해제 (이미 빈 값이면 건드리지 않는다 — 매 렌더 setValue 로 무한 루프에 걸리던 지점)
    if (areas.length === 0) {
      if (currentAreaId) setValue('selectedArea', '');
      return;
    }

    // 선택된 구역이 없거나, 선택된 구역이 목록에 없으면 첫 번째 구역 선택
    if (!currentAreaId || !areas.find(area => area.golfAreaId.toString() === currentAreaId)) {
      const firstAreaId = areas[0].golfAreaId.toString();
      setValue('selectedArea', firstAreaId);
    }
  }, [areas, setValue, watch]);

  // 선택된 구역 ID
  const selectedAreaId = watch('selectedArea');
  const selectedArea = useMemo(
    () => areas.find(area => area.golfAreaId.toString() === selectedAreaId),
    [areas, selectedAreaId],
  );

  // 타석 데이터 변환 함수
  const transformSpotsData = spotData => {
    let spotsArray = [];
    if (Array.isArray(spotData)) {
      spotsArray = spotData;
    } else if (Array.isArray(spotData?.spots)) {
      spotsArray = spotData.spots;
    } else if (spotData && typeof spotData === 'object' && spotData.id) {
      spotsArray = [spotData];
    }

    // 타석 데이터 포맷팅
    return spotsArray.map(spot => ({
      id: spot.id || spot.spotId || `S${spot.spotNumber || spot.spotNumber}`,
      spotNumber: spot.spotNumber,
      memberName: spot.memberName || null,
      endTime: spot.endTime || null,
      remainingMinutes: spot.remainingMinutes || null,
      waitingCount: spot.waitingCount || 0,
      status: spot.status || 'AVAILABLE',
      ...spot, // 기타 모든 속성 유지
    }));
  };

  // 선택된 구역의 타석 조회 (상태 변경 시 즉시 갱신 + 3초마다 자동 갱신)
  const {
    data: spotsData,
    isError: isSpotsError,
    error: spotsError,
  } = useQuery({
    queryKey: ['golfSpots', user?.token, selectedArea?.golfAreaId],
    queryFn: () => getGolfSpotsByAreaId(user.token, selectedArea.golfAreaId),
    enabled: !!user?.token && !!selectedArea?.golfAreaId,
    refetchOnWindowFocus: true, // 창 포커스 시 즉시 갱신 (상태 변경 감지)
    refetchOnMount: true, // 컴포넌트 마운트 시 즉시 갱신
    refetchInterval: AUTO_REFRESH_INTERVAL, // 3초마다 자동 갱신
    staleTime: 0, // 항상 최신 데이터로 간주 (즉시 갱신)
    select: transformSpotsData,
  });

  const spots = useMemo(() => spotsData || [], [spotsData]);
  // spots가 업데이트되면 selectedSpot도 최신화
  useEffect(() => {
    if (openDetailModal && selectedSpot?.id) {
      const updatedSpot = spots.find(spot => spot.id === selectedSpot.id);
      if (updatedSpot) {
        setSelectedSpot(updatedSpot);
      }
    }
  }, [spots, openDetailModal, selectedSpot?.id]);

  // 상태별 카운트 계산
  const waitingCount = useMemo(() => {
    return spots.reduce((sum, s) => sum + (s.waitingCount || s.waitings || 0), 0);
  }, [spots]);

  const handleAreaChange = e => {
    const areaId = e.target.value;
    setValue('selectedArea', areaId);
  };

  const handleBasicSettings = () => {
    setOpenBasicSettingModal(true);
  };

  // 타석 이동 mutation
  const moveSpotMutation = useMutation({
    mutationFn: spotMoveValue => updateGolfSpotReservationSpotMove(user.token, spotMoveValue),
    onSuccess: () => {
      toast.success('타석 이동이 완료되었습니다.');
      setIsMoveMode(false);
      setSourceSpotForMove(null);
      queryClient.invalidateQueries({ queryKey: ['golfSpots', user?.token, selectedArea?.golfAreaId] });
    },
    onError: error => {
      console.log('타석 이동 실패:', error);
      const errorMessage = error?.response?.data?.message;
      toast.error(errorMessage);
    },
  });

  const handleSpotClick = spot => {
    if (isMoveMode) {
      // 이동 모드일 때는 READY 상태인 타석만 선택 가능하게
      if (spot.spotStatus === 'READY') {
        // 같은 타석으로 이동하려는 경우 방지
        if (sourceSpotForMove?.id === spot.id) {
          toast.error('같은 타석으로는 이동할 수 없습니다.');
          return;
        }

        // reservationId 가져오기
        const reservationId = sourceSpotForMove?.reservation?.reservationId;

        if (!reservationId) {
          toast.error('예약 정보를 찾을 수 없습니다.');
          console.error('reservationId를 찾을 수 없습니다. sourceSpotForMove:', sourceSpotForMove);
          return;
        }
        const targetSpotId = spot.id || spot.spotId;
        if (!targetSpotId) {
          toast.error('타석 ID를 찾을 수 없습니다.');
          console.error('targetSpotId를 찾을 수 없습니다. spot:', spot);
          return;
        }

        const moveData = {
          targetSpotId,
          reservationId,
        };

        moveSpotMutation.mutate(moveData);
      } else {
        toast.error('예약 가능한 타석만 선택할 수 있습니다.');
      }
      return;
    }
    setSelectedSpot(spot);
    setOpenDetailModal(true);
  };

  const handleMoveSpot = spotData => {
    setIsMoveMode(true);
    setSourceSpotForMove(spotData);
  };

  const handleCancelMove = () => {
    setIsMoveMode(false);
    setSourceSpotForMove(null);
  };

  const handleDetailModalClose = () => {
    setOpenDetailModal(false);
    setSelectedSpot(null);
    // 모달이 닫힐 때 데이터 최신화
    queryClient.invalidateQueries({ queryKey: ['golfSpots', user?.token, selectedArea?.golfAreaId] });
  };

  // 예약 종료 시 로딩 상태 관리
  const handleReservationEndLoadingStart = () => {
    if (selectedSpot?.id) {
      const spotIdStr = String(selectedSpot.id);
      // 로딩 상태 추가
      setLoadingSpotIds(prev => {
        if (prev.includes(spotIdStr)) return prev;
        return [...prev, spotIdStr];
      });
    }
  };

  const handleReservationEndLoadingEnd = () => {
    if (selectedSpot?.id) {
      const spotIdStr = String(selectedSpot.id);
      // 데이터 갱신 후 로딩 상태 제거
      queryClient.invalidateQueries({ queryKey: ['golfSpots', user?.token, selectedArea?.golfAreaId] });
      // 1초 후 로딩 상태 제거 (데이터 갱신 시간 고려)
      setTimeout(() => {
        setLoadingSpotIds(prev => prev.filter(id => id !== spotIdStr));
      }, 3000);
    }
  };

  // 예약 성공 시 로딩 상태 설정
  const handleReservationSuccess = spotId => {
    if (spotId) {
      const spotIdStr = String(spotId);

      // 고유한 spot.id만 사용 (spotNumber나 spotName은 겹칠 수 있음)
      setLoadingSpotIds(prev => {
        if (prev.includes(spotIdStr)) return prev;
        return [...prev, spotIdStr];
      });

      // 3초 후 로딩 상태 제거
      setTimeout(() => {
        setLoadingSpotIds(prev => prev.filter(id => id !== spotIdStr));
      }, 3000);
    }
    // 모달 닫기
    setOpenDetailModal(false);
    setSelectedSpot(null);
    // 데이터 최신화
    queryClient.invalidateQueries({ queryKey: ['golfSpots', user?.token, selectedArea?.golfAreaId] });
  };

  // 스팟배치설정 페이지로 이동
  const handleMoveEditSpot = () => {
    navigate(`/store/${centerId}?tab=7`);
  };

  // 에러 발생 시 내용
  if (isAreasError) console.log('구역 조회 실패: ', areasError);
  if (isSpotsError) console.log('타석 조회 실패: ', spotsError);

  // 구역이 없을 때 표시할 UI
  if (areas.length === 0 && !selectedAreaId) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '800px',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Typography sx={{ fontSize: '24px', fontWeight: 'semibold', color: '#000' }}>
            {t('No created spot area')}
          </Typography>
          <Typography sx={{ fontSize: '16px', fontWeight: 'medium', color: '#737373' }}>
            {t('Create spot area')}
          </Typography>
          <img src="/images/icons/spot-not-found.svg" alt="spot-not-found" />
        </Box>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#0F7F85',
            color: '#FFFFFF',
            fontSize: '16px',
            fontWeight: '600',
            borderRadius: '8px',
            padding: '12px 24px',
            '&:hover': { backgroundColor: '#0D6B70' },
          }}
          onClick={handleMoveEditSpot}>
          스팟배치설정으로 이동
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: '32px' }}>
      {/* 필터 영역 */}
      <Box
        sx={{
          width: '100%',
          height: '112px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '32px',
        }}>
        {/* 왼쪽: 구역 선택 및 기본 설정 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <FormInputDropdown
            color="info"
            control={control}
            name="selectedArea"
            sx={{ width: '280px' }}
            rules={{ required: true }}
            size="small"
            required
            fullWidth
            variant="outlined"
            options={areas.map(area => ({
              value: area.golfAreaId.toString(),
              label: area.golfAreaInfo?.areaName || `구역 ${area.golfAreaId}`,
            }))}
            disabledItem={<MenuItem disabled>{t('Area Select')}</MenuItem>}
            onChange={handleAreaChange}
          />
          <Button
            sx={{
              width: '138px',
              height: '48px',
              backgroundColor: '#0F7F85',
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: '600',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              '&:hover': { backgroundColor: '#0F7F85' },
            }}
            onClick={handleBasicSettings}>
            <GolfIcon />
            {t('Basic settings')}
          </Button>
        </Box>

        {/* 중앙: 오늘 날짜 */}
        <Box
          sx={{
            minWidth: '160px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Typography sx={{ fontSize: '18px', fontWeight: '500', color: '#000' }}>{today}</Typography>
        </Box>

        {/* 오른쪽: 대기자 현황 및 상태 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* 대기자 현황 */}
          <Box
            sx={{
              width: '200px',
              height: '76px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
            }}>
            <Box
              onClick={() => setOpenWaitingListModal(true)}
              sx={{
                width: '200px',
                height: '76px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                borderRadius: '8px',
                boxShadow: '0px 0px 10px 0px rgba(82, 82, 82, 0.1)',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(15, 127, 133, 0.05)',
                },
              }}>
              <WaitingIcon />
              <Typography sx={{ fontSize: '20px', fontWeight: '500', color: '#000' }}>{t('Waiting')}</Typography>
              <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#0F7F85' }}>
                {waitingCount}
                {t('people')}
              </Typography>
            </Box>
          </Box>

          {/* 상태 표시 */}
          <Box
            sx={{
              display: 'flex',
              height: '76px',
              gap: '16px',
              padding: '24px 16px',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0px 0px 10px 0px rgba(82, 82, 82, 0.1)',
              borderRadius: '8px',
            }}>
            {/* 사용중 */}
            <Box
              sx={{
                width: '132px',
                minWidth: '132px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                borderRight: '2px solid #D9D9D9',
                pr: '16px',
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                <Box
                  sx={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #D9D9D9',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#000',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}>
                  {t('In use')}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#000' }}>
                {spots.filter(spot => spot.spotStatus === 'IN_USE' || spot.reservation?.status === 'RUNNING').length}
              </Typography>
            </Box>

            {/* 예약가능 */}
            <Box
              sx={{
                width: '132px',
                minWidth: '132px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                borderRight: '2px solid #D9D9D9',
                pr: '16px',
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                <Box
                  sx={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: '#0F7F85',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#000',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}>
                  {t('Available for reservation')}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#000' }}>
                {spots.filter(spot => spot.reservation?.status !== 'RUNNING' && spot.spotStatus === 'READY').length}
              </Typography>
            </Box>

            {/* 예약불가 */}
            <Box
              sx={{
                width: '132px',
                minWidth: '132px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                <Box
                  sx={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: '#A6A6A6',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#000',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}>
                  {t('Unavailable for reservation')}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#000' }}>
                {spots.filter(spot => spot.spotStatus === 'SUSPENDED' || spot.spotStatus === 'DISABLED').length}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 타석 그리드 */}
      <Box
        sx={{
          width: '100%',
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: `repeat(${
            areas.find(area => area.golfAreaId.toString() === watch('selectedArea'))?.golfAreaInfo?.columnCount || 6
          }, auto)`,
          gap: '80px',
          justifyContent: 'center',
        }}>
        {isMoveMode && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
              pointerEvents: 'none',
            }}
          />
        )}
        {spots.map(spot => {
          // 고유한 spot.id만 체크 (spotNumber나 spotName은 겹칠 수 있음)
          const isLoading = loadingSpotIds.includes(String(spot.id));

          // spot에 areaId 추가 (대기자 정보 조회를 위해)
          const spotWithAreaId = {
            ...spot,
            areaId: selectedArea?.golfAreaId,
          };

          return (
            <GolfSpotWidget
              key={spot.id}
              spot={spotWithAreaId}
              onClick={() => handleSpotClick(spot)}
              isMoveMode={isMoveMode}
              isDisabled={isMoveMode && spot.spotStatus !== 'READY'}
              isLoading={isLoading}
            />
          );
        })}
      </Box>

      {/* 이동 모드 메시지 */}
      {isMoveMode && (
        <Box
          sx={{
            position: 'fixed',
            bottom: '40px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1001,
            padding: '16px 32px',
            borderRadius: '8px',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Typography sx={{ fontSize: '24px', fontWeight: '600', color: '#ffffff' }}>
              이동할 타석을 선택해 주세요.
            </Typography>
            <Button
              onClick={handleCancelMove}
              sx={{
                backgroundColor: '#0F7F85',
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '8px',
                padding: '8px 16px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#0D6B70' },
              }}>
              취소
            </Button>
          </Box>
        </Box>
      )}

      {/* 타석 상세 모달 */}
      <GolfSpotDetailModal
        open={openDetailModal}
        handleClose={handleDetailModalClose}
        spotData={selectedSpot}
        onExtendTimeClick={() => setOpenExtendTimeModal(true)}
        onWaitingListClick={() => setOpenSpotWaitingListModal(true)}
        onMoveSpot={handleMoveSpot}
        onReservationSuccess={handleReservationSuccess}
        onLoadingStart={handleReservationEndLoadingStart}
        onLoadingEnd={handleReservationEndLoadingEnd}
      />

      {/* 기본 설정 모달 */}
      <GolfSpotBasicSettingModal
        open={openBasicSettingModal}
        handleClose={() => setOpenBasicSettingModal(false)}
        spotData={selectedArea}
      />
      {/* 시간 연장 모달 */}
      <GolfExtendTimeModal
        open={openExtendTimeModal}
        handleClose={() => setOpenExtendTimeModal(false)}
        spotData={selectedSpot}
      />

      {/* 대기자 현황 모달 */}
      <GolfSpotWaitingAllListModal
        open={openWaitingListModal}
        handleClose={() => setOpenWaitingListModal(false)}
        spots={spots}
        areaId={selectedArea?.golfAreaId}
      />

      {/* 타석별 대기자 목록 모달 */}
      <GolfSpotWatingListModal
        open={openSpotWaitingListModal}
        handleClose={() => setOpenSpotWaitingListModal(false)}
        spotData={selectedSpot}
      />
    </Box>
  );
};

export default GolfSpotManagement;
